import type { User } from '@/modules/users/types/User';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import * as Sentry from '@sentry/react';
import { merge } from 'lodash-es';
import logger from '@/modules/logger/constants/logger';

export interface AuthUserStoreState {
  /**
   * Authentication status
   *
   * Why do we persist the user object? It's because we want to set
   * the user object in Sentry as soon as possible. But this user object won't be used
   * immediately in the app until the authentication process is complete.
   */
  status: 'uninitialized' | 'loading' | 'authenticated' | 'unauthenticated';

  /**
   * The logged in user object.
   */
  user?: User;
}

interface AuthUserStoreActions {
  /**
   * Set the user
   * @param user - The user
   */
  setUser: (user: User) => void;

  /**
   * Clear the user
   */
  clearUser: () => void;
}

const createStore: Parameters<
  typeof immer<AuthUserStoreState & AuthUserStoreActions>
>[0] = (set) => ({
  status: 'uninitialized',
  user: undefined,
  setUser(user) {
    set((state) => {
      const sentryUser = {
        id: user.id,
        username: user.username ?? undefined,
        userNumber: user.userNumber,
        email: user.email ?? undefined,
      };
      logger.debug('auth.login', 'Setting Sentry user...', sentryUser);
      Sentry.setUser(sentryUser);
      state.user = user;
      state.status = 'authenticated';
    });
  },
  clearUser() {
    set((state) => {
      logger.debug('auth.logout', 'Clearing Sentry user...');
      Sentry.setUser(null);
      state.user = undefined;
      state.status = 'unauthenticated';
    });
  },
});

export default create(
  devtools(
    persist(immer(createStore), {
      name: 'auth_user',
      storage: createJSONStorage(() => localStorage),
      merge: (persistedState, currentState) =>
        merge(currentState, persistedState),
      // exclude the isUserValidated property
      partialize: (state) => ({
        user: state.user,
      }),
      onRehydrateStorage: () => (state) => {
        logger.debug('auth.startup', 'Rehydrating auth user store...');
        if (state?.user) {
          const user = {
            id: state.user.id,
            username: state.user.username ?? undefined,
            userNumber: state.user.userNumber,
            email: state.user.email ?? undefined,
          };
          logger.debug('auth.startup', 'Setting Sentry user...', user);
          Sentry.setUser(user);
        }
      },
    })
  )
);
